import { notifications } from '@mantine/notifications';
import i18n from './i18n';
import { APP_VERSION } from './version';

/**
 * Client update check.
 *
 * Keyed off the deployed build's /version.txt (which the build emits) rather
 * than the API's Current-Version header. Why:
 *   - version.txt is the CLIENT's own version lineage, so it's directly
 *     comparable to the running APP_VERSION (the API's number is a different
 *     lineage and isn't comparable).
 *   - it's fetched no-store, so it can't be fooled by a service-worker-cached
 *     response (the bug behind the earlier phantom "new version" toast).
 *   - it carries the actual deployed version number, so the prompt can name it.
 *
 * Automatic: a periodic + on-tab-focus poll. Manual: the version in the footer,
 * on every screen including Sign In, and the "Check for updates" menu item.
 *
 * THE PROMPT IS NOT DISMISSABLE.
 * It used to be a toast with a close button, so it could be waved away — and a
 * copy that stays behind keeps the route table it was built with. That is how a
 * deploy adding /invitations/{token} left an invitee looking at "Not Found":
 * their app was five builds old and had declined to say so. Updating is now a
 * required acknowledgement — one OK button, no close, no Escape, no clicking
 * outside — which is how GOS and LOS have always done it.
 *
 * It is still an acknowledgement rather than a silent reload, because this runs
 * in warehouses: yanking the page out from under somebody mid-count loses their
 * work. They cannot refuse, but they do get to finish the sentence they are on.
 */

const UPDATE_TOAST_ID = 'app-update';
// Which version this session has already tried to apply. Survives the reload an attempt causes and
// dies with the app, which is exactly the lifetime wanted: reopening should get a clean go.
const ATTEMPT_KEY = 'invetrak.updateAttempt';
const VERSION_PATTERN = /^\d+\.\d+\.\d+/;
const CHECK_INTERVAL_MS = 30 * 60 * 1000;

let applyUpdate: (() => Promise<void>) | null = null;
let armed = false;

/**
 * An attempt is in flight, and it ends in a reload.
 *
 * WHY THIS EXISTS — the bug it fixes was reported three times.
 *
 * Applying an update is SLOW: registerSW waits up to ten seconds for a new worker to install and
 * five more for it to take control, so applyUpdate can be fifteen seconds from returning. Nothing
 * stopped a second check running inside that window, and there are six callers — the modal on
 * mount, the service worker's onNeedRefresh, the interval, tab focus, and two manual buttons.
 *
 * Each one that ran while an attempt was already going saw the same "one attempt recorded, still
 * behind" state, escalated again, and incremented the counter. Two or three of those and the next
 * check reported "the app could not update itself" — WHILE THE FIRST ATTEMPT WAS STILL WORKING and
 * about to reload successfully. Which is exactly what a user kept seeing: the error, and then the
 * update applying on its own without them closing anything.
 *
 * The counter was never wrong about what it counted. It was counting calls, not attempts.
 */
let applying = false;

/** The pending update, or null. Read through subscribeToUpdate/getRequiredUpdate. */
let required: string | null = null;
const listeners = new Set<(version: string | null) => void>();

/** Registered by sw/registerSW.ts so a reload activates the new SW bundle. */
export function setUpdateApplier(fn: () => Promise<void>): void {
  applyUpdate = fn;
}

// ---------------------------------------------------------------- the store --
// The value lives here; the SIGNAL goes out as a window event.
//
// A module-level listener set alone is a trap: a module can be instantiated more
// than once — Vite's dev server hands out `?t=` variants on every hot update, and
// a manualChunks split can duplicate one in a production bundle — and then the
// half that sets the value and the half a component subscribed to are different
// objects, with nothing to show for it. That duplication is real and was observed
// here on 2026-08-19 while debugging; it was not the cause of the symptom being
// chased at the time, but it is a live hazard for anything that has to cross
// module boundaries reliably. There is exactly one `window`, so the event cannot
// miss. The set is kept alongside it for same-instance callers.

const UPDATE_EVENT = 'invetrak:update-required';

export function subscribeToUpdate(onChange: (version: string | null) => void): () => void {
  const handler = (e: Event) => onChange((e as CustomEvent<string | null>).detail ?? null);
  window.addEventListener(UPDATE_EVENT, handler);
  listeners.add(onChange);
  return () => {
    window.removeEventListener(UPDATE_EVENT, handler);
    listeners.delete(onChange);
  };
}

/** The deployed version awaiting acknowledgement, or null. */
export function getRequiredUpdate(): string | null {
  return required;
}

function setRequiredUpdate(version: string | null): void {
  if (required === version) return;
  required = version;
  listeners.forEach((fn) => fn(version));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<string | null>(UPDATE_EVENT, { detail: version }));
  }
}

/** For tests, which need the module's state not to leak between cases. */
export function resetUpdateStateForTests(): void {
  required = null;
  applyUpdate = null;
  applying = false;
}

// ------------------------------------------------------------------ applying --

/**
 * Activate a waiting bundle and reload, keeping the current URL.
 *
 * Resolves WITHOUT reloading when the update could not be installed — see
 * registerSW's applyUpdate. Callers should treat "we are still here" as failure,
 * because the alternative is trapping somebody in a dialog whose only button
 * does nothing.
 */
function rememberAttempt(version: string | null): void {
  try {
    if (!version) {
      sessionStorage.removeItem(ATTEMPT_KEY);
      return;
    }
    sessionStorage.setItem(
      ATTEMPT_KEY,
      JSON.stringify({ v: version, n: attemptsFor(version) + 1 }),
    );
  } catch {
    // Private mode, or storage disabled. The escalation below just does not engage, and every
    // attempt looks like a first one — which errs towards trying again rather than complaining.
  }
}

/**
 * Applies the pending update.
 *
 * @param version the version being chased, when it is not the one in the store — the automatic
 * escalation below runs without a prompt having set it.
 */
export async function applyPendingUpdate(version?: string): Promise<void> {
  // At most once per load. Not a debounce — there is nothing to debounce, because a successful
  // attempt never returns here at all: it reloads the page.
  if (applying) return;
  applying = true;

  rememberAttempt(version ?? required);
  if (applyUpdate) {
    await applyUpdate();
    return;
  }
  window.location.reload();
}

/** The version the server is serving, or null if it could not be read. */
export async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.txt', { cache: 'no-store' });
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    // Guard against a misconfigured SPA fallback returning index.html.
    return VERSION_PATTERN.test(text) ? text : null;
  } catch {
    return null;
  }
}

/**
 * How many times this session has tried to reach a given version.
 *
 * A count, not a flag. Unregistering a service worker does not take effect while the page it
 * controls is still open, so the reload straight after an unregister is often STILL served by the
 * old worker — the version has not moved yet, even though the fix has been applied and the next
 * load will get it. A flag turned that into "the app could not update itself" on a machine that
 * then updated perfectly well one load later, which is exactly what a user reported.
 */
function attemptsFor(version: string): number {
  try {
    const raw = sessionStorage.getItem(ATTEMPT_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { v?: string; n?: number };
    return parsed.v === version ? (parsed.n ?? 0) : 0;
  } catch {
    return 0;
  }
}

/**
 * The update could not be applied, and a reload would not have helped.
 *
 * Only reachable when a service worker is already in charge and no successor could be installed.
 * `contentType` is what /sw.js answered with when it was not a script — the one cause the page can
 * diagnose from inside, and the one that actually happened.
 *
 * Clears the required-update state as well: an OK button that cannot work must not be the only way
 * out of the dialog, or a broken deployment locks every user out of the whole app.
 */
export function reportUpdateBlocked(contentType: string | null): void {
  setRequiredUpdate(null);
  notifications.show({
    id: UPDATE_TOAST_ID,
    withCloseButton: true,
    autoClose: false,
    color: 'red',
    title: i18n.t('shell.updateBlockedTitle'),
    message: contentType
      ? i18n.t('shell.updateBlockedBadScript', { contentType })
      : i18n.t('shell.updateBlockedBody'),
  });
}

/**
 * Compare the deployed version to the running one.
 * @param manual when true, also surface "up to date" / "couldn't check".
 */
export async function checkForUpdate(manual = false): Promise<void> {
  const deployed = await fetchDeployedVersion();

  if (!deployed) {
    if (manual) {
      notifications.show({ message: i18n.t('shell.checkFailed'), color: 'yellow' });
    }
    return;
  }

  if (deployed !== APP_VERSION) {
    // An attempt is already running and will reload when it is done. Saying anything now — another
    // escalation, or a complaint — is describing a race, not the state of the app.
    if (applying) return;

    const attempts = attemptsFor(deployed);

    // Already asked to update to this version, and back here still behind it. Do NOT ask again and
    // do NOT complain: consent was given the first time, so finish the job. The second attempt is
    // the one that unregisters the worker and empties its caches, and the reload after that is
    // served from the network.
    if (attempts === 1) {
      void applyPendingUpdate(deployed);
      return;
    }

    // Two goes, including the one that removes the worker outright, and the build still has not
    // moved. Now it is worth saying so — there is nothing left to try from in here.
    if (attempts >= 2) {
      reportUpdateBlocked(null);
      return;
    }

    setRequiredUpdate(deployed);
    return;
  }

  // Running the deployed version, so whatever was attempted worked. Forget it.
  rememberAttempt(null);
  setRequiredUpdate(null);
  if (manual) {
    notifications.show({
      message: i18n.t('shell.upToDate', { version: APP_VERSION }),
      color: 'green',
    });
  }
}

/** Arm the periodic + on-focus automatic check. Idempotent. */
export function startAutoVersionCheck(): void {
  if (armed || typeof window === 'undefined') return;
  armed = true;
  window.setInterval(() => void checkForUpdate(false), CHECK_INTERVAL_MS);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate(false);
  });
}
